import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import {
  createInvoiceFollowup,
  getInvoiceFollowupErrorMessage,
} from "../../utilis/invoiceFollowupApi";
import { showInvoiceFollowupSuccessSwal } from "../../utilis/invoiceFollowupHelpers";
import {
  CONTEXT_INVOICE_CLIENT,
  CONTEXT_INVOICE_COMMISSION,
  getLocalDateInputValue,
} from "../../utilis/followupConstants";
import { emitStaffNotificationRefresh } from "../../utilis/notificationSocket";

const emptyForm = () => ({
  assignedId: "",
  description: "",
  date: getLocalDateInputValue(),
});

function InvoiceFollowupModal({
  show,
  contextType = CONTEXT_INVOICE_COMMISSION,
  financeInvoiceId = null,
  commissionPlanId = null,
  clientId = null,
  providerId = null,
  applicationId = null,
  applicationCourseId = null,
  users = [],
  socket = null,
  user = null,
  onClose,
  onSuccess,
}) {
  const [form, setForm] = useState(emptyForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      setForm(emptyForm());
      setFieldErrors({});
      setSubmitError("");
    }
  }, [show]);

  const sortedUsers = useMemo(
    () =>
      [...(users || [])].sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || "")),
      ),
    [users],
  );

  const validate = () => {
    const errors = {};
    if (!financeInvoiceId) {
      errors.financeInvoiceId =
        "A saved finance invoice is required before adding a follow-up.";
    }
    if (!form.assignedId) errors.assignedId = "Please select a user.";
    if (!String(form.description || "").trim()) {
      errors.description = "Description is required.";
    }
    if (!form.date) errors.date = "Follow-up date is required.";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!validate()) return;

    if (!financeInvoiceId) {
      setSubmitError(
        "A saved finance invoice is required before adding a follow-up.",
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        assignedid: form.assignedId,
        context_type: contextType,
        description: form.description,
        date: form.date,
        client_id: clientId || undefined,
        provider_id: providerId || undefined,
        application_id: applicationId || undefined,
        application_course_id: applicationCourseId || undefined,
        finance_invoice_id: financeInvoiceId,
        commission_plan_id: commissionPlanId || undefined,
      };
      const data = await createInvoiceFollowup(payload);
      if (data?.error) {
        setSubmitError(data.error);
        return;
      }
      const assigneeName =
        data?.data?.assignee_name ||
        sortedUsers.find((u) => String(u.id) === String(form.assignedId))?.name ||
        "";
      showInvoiceFollowupSuccessSwal(data, assigneeName);
      emitStaffNotificationRefresh(socket, user);
      onSuccess?.(data);
      onClose?.();
    } catch (err) {
      setSubmitError(getInvoiceFollowupErrorMessage(err, err?.response?.data));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHide = () => {
    if (!isSubmitting) onClose?.();
  };

  return (
    <Modal
      show={show}
      onHide={handleHide}
      centered
      backdrop="static"
      container={typeof document !== "undefined" ? document.body : undefined}
      className="cms-inv-followup-bootstrap"
      dialogClassName="cms-inv-followup-modal-bootstrap-dialog"
    >
      <Modal.Header closeButton>
        <Modal.Title>Add follow-up</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          {submitError ? (
            <div
              className="alert alert-danger py-2 mb-3"
              style={{ fontSize: "13px" }}
              role="alert"
            >
              {submitError}
            </div>
          ) : null}

          <div className="cms-inv-followup-field">
            <label className="cms-inv-followup-label" htmlFor="inv-fu-assignee">
              Assign to
            </label>
            <select
              id="inv-fu-assignee"
              className="cms-inv-followup-select form-select"
              value={form.assignedId}
              onChange={(e) =>
                setForm((f) => ({ ...f, assignedId: e.target.value }))
              }
            >
              <option value="">Please select</option>
              {sortedUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
            {fieldErrors.assignedId ? (
              <p className="cms-inv-followup-error text-danger small mb-0 mt-1">
                {fieldErrors.assignedId}
              </p>
            ) : null}
          </div>

          <div className="cms-inv-followup-field">
            <label
              className="cms-inv-followup-label"
              htmlFor="inv-fu-description"
            >
              Description
            </label>
            <textarea
              id="inv-fu-description"
              className="cms-inv-followup-textarea form-control"
              rows={4}
              value={form.description}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
              placeholder="What needs to be done?"
            />
            {fieldErrors.description ? (
              <p className="cms-inv-followup-error text-danger small mb-0 mt-1">
                {fieldErrors.description}
              </p>
            ) : null}
          </div>

          <div className="cms-inv-followup-field mb-0">
            <label className="cms-inv-followup-label" htmlFor="inv-fu-date">
              Follow-up date
            </label>
            <input
              id="inv-fu-date"
              type="date"
              className="cms-inv-followup-input form-control"
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
            />
            {fieldErrors.date ? (
              <p className="cms-inv-followup-error text-danger small mb-0 mt-1">
                {fieldErrors.date}
              </p>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleHide} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="success"
            type="submit"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? "Saving…" : "Add follow-up"}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}

export { CONTEXT_INVOICE_CLIENT, CONTEXT_INVOICE_COMMISSION };
export default InvoiceFollowupModal;
